import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { 
  Send, 
  Trash2, 
  Copy, 
  Check,
  Terminal,
  RefreshCw,
  User,
  Bot,
  ClipboardCheck,
  AlertTriangle,
  Activity,
  CheckCircle,
  XCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [osType, setOsType] = useState("linux");
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem('ai_session_id');
    return saved || `session-${Date.now()}`;
  });
  const [copiedId, setCopiedId] = useState(null);
  const [queuedCommands, setQueuedCommands] = useState({});
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [odooContext, setOdooContext] = useState(null);
  const messagesEndRef = useRef(null);

  // Save session ID to localStorage
  useEffect(() => {
    localStorage.setItem('ai_session_id', sessionId);
  }, [sessionId]);

  // Load servers for command targeting
  useEffect(() => {
    axios.get(`${API}/servers`).then(res => {
      setServers(res.data);
      const sshServer = res.data.find(s => s.ssh_configured);
      if (sshServer) setSelectedServer(sshServer.id);
    }).catch(() => {});

    // Load Odoo context
    axios.get(`${API}/chat/odoo-context`).then(res => {
      setOdooContext(res.data);
    }).catch(() => {});
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChatHistory();
  }, [sessionId]);

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    // Optimistically add user message
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: userMessage,
      os_type: osType,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        os_type: osType,
        context: `User is asking about ${osType.toUpperCase()} server administration`
      });

      // Add assistant response
      const assistantMsg = {
        id: `msg-${Date.now()}`,
        role: "assistant",
        content: response.data.response,
        os_type: osType,
        timestamp: response.data.timestamp
      };
      setMessages(prev => [...prev.slice(0, -1), tempUserMsg, assistantMsg]);
      
    } catch (error) {
      toast.error("Failed to get response from AI");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = async () => {
    if (window.confirm("Clear all chat history?")) {
      try {
        await axios.delete(`${API}/chat/${sessionId}`);
        setMessages([]);
        setQueuedCommands({});
        toast.success("Chat cleared");
      } catch (error) {
        toast.error("Failed to clear chat");
      }
    }
  };

  const handleNewSession = () => {
    const newId = `session-${Date.now()}`;
    setSessionId(newId);
    localStorage.setItem('ai_session_id', newId);
    setMessages([]);
    setQueuedCommands({});
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const queueForApproval = async (command, description) => {
    const commandKey = command.slice(0, 30);
    if (queuedCommands[commandKey]) {
      toast.info("Command already queued for approval");
      return;
    }

    try {
      // First analyze the command risk
      const analysisResponse = await axios.post(`${API}/approvals/analyze-command?command=${encodeURIComponent(command)}&os_type=${osType}`);
      const { risk_level, warning } = analysisResponse.data;

      // Create approval request with server_id
      const payload = {
        command,
        description: description || `AI-generated command from chat session`,
        os_type: osType,
        risk_level,
        ai_warning: warning
      };
      if (selectedServer) {
        payload.server_id = selectedServer;
      }
      await axios.post(`${API}/approvals`, payload);

      setQueuedCommands(prev => ({ ...prev, [commandKey]: true }));
      
      const serverName = servers.find(s => s.id === selectedServer)?.name;
      const riskMessage = risk_level === 'critical' ? 'CRITICAL risk' : 
                          risk_level === 'high' ? 'HIGH risk' : 
                          risk_level === 'medium' ? 'MEDIUM risk' : 'LOW risk';
      
      toast.success(`Command queued (${riskMessage})${serverName ? ` for ${serverName}` : ''}`, {
        description: "Go to Approvals page to review and execute"
      });
    } catch (error) {
      toast.error("Failed to queue command for approval");
    }
  };

  const extractCodeBlocks = (content) => {
    const codeBlockRegex = /```(?:bash|powershell|shell|cmd)?\n?([\s\S]*?)```/g;
    const blocks = [];
    let match;
    while ((match = codeBlockRegex.exec(content)) !== null) {
      blocks.push(match[1].trim());
    }
    return blocks;
  };

  const suggestionPrompts = [
    "Show me disk usage on all partitions",
    "Check Odoo service status and health",
    "Why is Odoo running slow? Check performance",
    "Show recent failed login attempts",
    "Check Apache/Nginx errors for Odoo",
    "Analyze PostgreSQL slow queries for Odoo"
  ];

  return (
    <div className="p-6 h-[calc(100vh-80px)] flex flex-col" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">AI Assistant</h1>
          <p className="text-zinc-400 text-sm mt-1">Natural language to server commands</p>
          {odooContext?.has_context && (
            <div className="flex items-center gap-2 mt-2" data-testid="odoo-context-indicator">
              <Activity className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-medium">Odoo Context Active</span>
              <span className="text-[10px] text-zinc-500">|</span>
              {odooContext.servers?.map((s, i) => {
                const isHealthy = s.health === "healthy" || s.health === "fixed";
                const isCritical = s.health === "critical";
                return (
                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded border ${
                    isHealthy ? "bg-green-500/10 text-green-400 border-green-500/20" :
                    isCritical ? "bg-red-500/10 text-red-400 border-red-500/20" :
                    "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                  }`}>
                    {isHealthy ? <CheckCircle className="w-2.5 h-2.5 inline mr-0.5" /> :
                     isCritical ? <XCircle className="w-2.5 h-2.5 inline mr-0.5" /> :
                     <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />}
                    {s.name}: {s.health}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedServer} onValueChange={setSelectedServer}>
            <SelectTrigger className="w-48 bg-black/50 border-white/10 text-white text-sm" data-testid="server-select">
              <SelectValue placeholder="No server selected" />
            </SelectTrigger>
            <SelectContent className="bg-[#0c0c0e] border-white/10">
              {servers.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} {s.ssh_configured ? '(SSH)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={osType} onValueChange={setOsType}>
            <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white" data-testid="os-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0c0c0e] border-white/10">
              <SelectItem value="linux">Linux</SelectItem>
              <SelectItem value="windows">Windows</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            onClick={handleNewSession}
            data-testid="new-session-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            New Chat
          </Button>
          <Button
            variant="outline"
            className="bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400"
            onClick={handleClearChat}
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Chat Container */}
      <Card className="flex-1 bg-[#0c0c0e] border-white/5 flex flex-col overflow-hidden">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-4">
                <Terminal className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-medium text-white mb-2">Server Admin AI</h3>
              <p className="text-zinc-400 max-w-md mb-6">
                Ask me anything about server administration. I can generate commands, troubleshoot issues, 
                and guide you through installations.
              </p>
              <div className="grid grid-cols-2 gap-2 max-w-lg">
                {suggestionPrompts.map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(prompt)}
                    className="p-3 text-left text-sm bg-black/30 border border-white/5 rounded-lg hover:border-blue-500/30 hover:bg-blue-500/5 transition-colors text-zinc-300"
                    data-testid={`suggestion-${idx}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex gap-3 slide-in ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${idx}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                  )}
                  <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                    <div
                      className={`rounded-xl p-4 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-black/40 border border-white/10 text-zinc-100'
                      }`}
                    >
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown
                            components={{
                              code({ node, inline, className, children, ...props }) {
                                const codeString = String(children).replace(/\n$/, '');
                                const commandKey = codeString.slice(0, 30);
                                const isQueued = queuedCommands[commandKey];
                                
                                if (inline) {
                                  return <code className="bg-black/50 px-1.5 py-0.5 rounded text-blue-300 font-mono text-sm" {...props}>{children}</code>;
                                }
                                return (
                                  <div className="relative group my-2">
                                    <pre className="bg-black/60 border border-white/10 rounded-lg p-4 overflow-x-auto">
                                      <code className="font-mono text-sm text-green-300" {...props}>{children}</code>
                                    </pre>
                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={() => copyToClipboard(codeString, `code-${idx}-${codeString.slice(0,10)}`)}
                                        className="p-1.5 rounded bg-white/10 hover:bg-white/20"
                                        title="Copy command"
                                      >
                                        {copiedId === `code-${idx}-${codeString.slice(0,10)}` ? (
                                          <Check className="w-4 h-4 text-green-400" />
                                        ) : (
                                          <Copy className="w-4 h-4 text-zinc-400" />
                                        )}
                                      </button>
                                      <button
                                        onClick={() => queueForApproval(codeString, `Command from AI Assistant`)}
                                        className={`p-1.5 rounded ${isQueued ? 'bg-green-500/20' : 'bg-orange-500/20 hover:bg-orange-500/30'}`}
                                        title={isQueued ? "Queued for approval" : "Queue for approval"}
                                        disabled={isQueued}
                                      >
                                        {isQueued ? (
                                          <Check className="w-4 h-4 text-green-400" />
                                        ) : (
                                          <ClipboardCheck className="w-4 h-4 text-orange-400" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                );
                              },
                              pre: ({ children }) => <>{children}</>,
                              p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
                              ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                              li: ({ children }) => <li className="text-zinc-300">{children}</li>,
                              h1: ({ children }) => <h1 className="text-xl font-bold mb-2 text-white">{children}</h1>,
                              h2: ({ children }) => <h2 className="text-lg font-semibold mb-2 text-white">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-base font-medium mb-1 text-white">{children}</h3>,
                              strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 px-1">
                      {new Date(msg.timestamp).toLocaleTimeString()}}
                    </p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-zinc-300" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </CardContent>

        {/* Input Area */}
        <div className="p-4 border-t border-white/5">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask about ${osType} server administration...`}
              className="flex-1 bg-black/50 border-white/10 text-white resize-none font-mono text-sm min-h-[60px] max-h-[120px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={loading || !input.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white h-auto px-6 glow-blue disabled:opacity-50"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
          <p className="text-xs text-zinc-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </Card>
    </div>
  );
}
